import React from "react";
import sales_team_office from '../../Images/sales_team_office.webp';
import sales_manager_scene_under_200kb from '../../Images/meeting_converted.webp';
import requirements_team_collaboration from '../../Images/requirements_team_collaboration.webp'
/**
 * Sales Manager/in - Landing Section (React + Tailwind)
 * Notes:
 * - Replace image paths with your own.
 * - This matches the layout/style of the generated design:
 *   left hero image, right info card with bullet checks + CTA + CV mock image,
 *   then requirements + bottom sales areas cards + right team image.
 */

const CheckItem = ({ title, desc }) => (
  <div className="flex gap-3">
    <span className="mt-1 inline-flex h-7 w-7 items-center justify-center rounded-full  ring-1 ring-emerald-100">
      <svg
        viewBox="0 0 20 20"
        className="h-4 w-4 text-emerald-600"
        fill="currentColor"
        aria-hidden="true"
      >
        <path
          fillRule="evenodd"
          d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.1 7.168a1 1 0 0 1-1.42 0L3.29 9.95a1 1 0 1 1 1.42-1.4l3.19 3.23 6.39-6.45a1 1 0 0 1 1.414-.04z"
          clipRule="evenodd"
        />
      </svg>
    </span>

    <div>
      <p className="font-semibold text-slate-900">{title}</p>
      {desc ? <p className="mt-1 text-sm text-slate-600">{desc}</p> : null}
    </div>
  </div>
);

const Pill = ({ children, tone = "green" }) => {
  const map = {
    green: "bg-lime-200/70 text-slate-900 ring-lime-300",
    teal: "bg-teal-200/70 text-slate-900 ring-teal-300",
    sky: "bg-sky-200/70 text-slate-900 ring-sky-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${map[tone]}`}
    >
      {children}
    </span>
  );
};

export default function SalesManager() {
  // Replace these with your real assets
  const heroImage =
    sales_team_office;
  const cvMock =
    sales_manager_scene_under_200kb;
  const teamImage =
    requirements_team_collaboration;

  return (
    <section className="relative overflow-hidden bg-slate-50">
      {/* soft background accents */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-sky-200/25 blur-3xl" />
        <div className="absolute right-0 top-32 h-72 w-72 rounded-full bg-[#8ec700]/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-lime-200/25 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(#cbd5e1_1px,transparent_1px)] [background-size:22px_22px] opacity-[0.18]" />
      </div>

      <div className="relative mx-auto w-[92%] max-w-6xl py-10 lg:py-14">
        {/* Top / Hero */}
        <div className="grid gap-8 lg:grid-cols-12 lg:items-start">
          {/* Left Hero */}
          <div className="lg:col-span-7">
            <p className="text-sm font-semibold tracking-wide text-slate-600">
              Karrierechance:
            </p>

            <h2 className="mt-2 h2-mb h2-md h2-lg h2-2xl">
              Sales Manager/in
            </h2>

            <p className="mt-4 max-w-2xl text-base text-slate-600 sm:text-lg">
              Steige in die Welt des Vertriebs ein und werde Expert:in im Bereich
              Sales!
            </p>

            <div className="mt-6 overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] ring-1 ring-[#cac6c6]">
              <img
                src={heroImage}
                alt="Office / Sales"
                className="h-[260px] w-full object-cover sm:h-[320px] lg:h-[360px]"
              />
            </div>
          </div>

          {/* Right Card */}
          <div className="lg:col-span-5">
            <div className="rounded-3xl bg-white/80 p-6 shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] ring-1 ring-[#cac6c6] backdrop-blur">
              <h3 className="h3-mb h3-md h3-lg h3-2xl">
                SALES MANAGER/IN
              </h3>

              <p className="mt-3 text-sm leading-6 text-slate-600">
                Betreue verschiedenste Unternehmen und erlange breites
                Branchenwissen. Dein Know-how wächst mit jedem Kundenkontakt und
                du wirst zum/zur Expert:in im Vertrieb.
              </p>

              <div className="mt-5 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] h-5 w-5 text-emerald-600">
                    ✓
                  </span>
                  <span className="font-medium text-slate-800">
                    Vielfältige Kundenprojekte
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] h-5 w-5 text-emerald-600">
                    ✓
                  </span>
                  <span className="font-medium text-slate-800">
                    Attraktives Gehalt
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] h-5 w-5 text-emerald-600">
                    ✓
                  </span>
                  <span className="font-medium text-slate-800">
                    Flexible Arbeitszeiten
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="mt-[2px] h-5 w-5 text-emerald-600">
                    ✓
                  </span>
                  <span className="font-medium text-slate-800">
                    Top Karrieremöglichkeiten
                  </span>
                </div>
              </div>

              <button className="mt-6 w-full rounded-xl bg-[#0b2b40] px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#0b2b40]/700 focus:outline-none focus:ring-4 focus:ring-teal-200">
                JETZT BEWERBEN
              </button>

              <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
                {/* CV mock (tilted look) */}
                <div className="relative">
                  <div className="absolute -left-2 -top-2 h-12 w-12 rounded-2xl bg-lime-200/80 blur-[1px]" />
                  <div className="absolute -right-4 bottom-10 h-14 w-14 rounded-full bg-[#8ec700]/70 blur-[2px]" />
                  <div className="overflow-hidden rounded-2xl bg-white shadow-[0_18px_40px_-28px_rgba(15,23,42,0.45)] ring-1 ring-[#cac6c6]">
                    <img
                      src={cvMock}
                      alt="CV Mock"
                      className="h-40 w-full object-cover sm:h-44 lg:h-48"
                    />
                  </div>

                  {/* small check bubble */}
                  <div className="absolute -left-1 -bottom-4 inline-flex items-center gap-2 rounded-full bg-yellow-200 px-3 py-2 text-xs font-bold text-slate-900 shadow-sm ring-1 ring-yellow-300">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-yellow-300">
                      ✓
                    </span>
                    Platz für Karriere machen!
                  </div>
                </div>

                {/* optional small stats (can remove) */}
                <div className="hidden rounded-2xl bg-slate-50 p-4 ring-1 ring-[#cac6c6] sm:block lg:hidden">
                  <div className="flex flex-wrap gap-2">
                    <Pill tone="green">Onboarding</Pill>
                    <Pill tone="teal">Coaching</Pill>
                    <Pill tone="sky">Wachstum</Pill>
                  </div>
                  <p className="mt-3 text-sm text-slate-600">
                    Starte mit strukturierter Einarbeitung & echten
                    Entwicklungschancen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Requirements + Bottom */}
        <div className="mt-10 grid gap-8 lg:mt-14 lg:grid-cols-12">
          {/* Left Requirements */}
          <div className="lg:col-span-7">
            <h3 className="h3-mb h3-md h3-lg h3-2xl">
              UNSERE ANFORDERUNGEN
            </h3>
            <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
              Bring dein Talent ein und starte deine Karriere als Sales
              Manager/in! Wir suchen Menschen, die…
            </p>

            <div className="mt-6 space-y-5 rounded-3xl bg-white/70 p-6 ring-1 ring-[#cac6c6] backdrop-blur">
              <CheckItem
                title="kommunikativ und offen sind"
                desc="Du gehst gern auf Menschen zu und bringst Themen verständlich rüber."
              />
              <CheckItem
                title="motiviert und zielstrebig arbeiten"
                desc="Du bleibst dran, bis ein Ziel erreicht ist."
              />
              <CheckItem
                title="PC-affin und eigenständig sind"
                desc="Du findest dich schnell in Tools & Prozessen zurecht."
              />
              <CheckItem
                title="empathisch und teamfähig sind"
                desc="Du arbeitest gern mit anderen und hörst aktiv zu."
              />
            </div>

            {/* Sales Areas */}
            <div className="mt-8 rounded-3xl bg-white/70 p-6 ring-1 ring-[#cac6c6] backdrop-blur">
              <h3 className="h3-mb h3-md h3-lg h3-2xl">
                DEINE POTENZIELLEN VERKAUFSBEREICHE
              </h3>
              <p className="mt-2 text-sm text-slate-600">
                Diese spannenden Bereiche erwarten dich.
              </p>

              <div className="mt-5 space-y-3">
                <div className="rounded-2xl bg-gradient-to-r from-lime-200/80 to-lime-100/30 p-4 ring-1 ring-lime-300/60">
                  <p className="font-extrabold tracking-tight text-slate-900">
                    BEWERTUNGSLÖSCHUNGEN
                  </p>
                </div>
                <div className="rounded-2xl bg-gradient-to-r from-lime-200/80 to-lime-100/30 p-4 ring-1 ring-lime-300/60">
                  <p className="font-extrabold tracking-tight text-slate-900">
                    WEBDESIGN
                  </p>
                </div>
                <div className="rounded-2xl bg-gradient-to-r from-lime-200/80 to-lime-100/30 p-4 ring-1 ring-lime-300/60">
                  <p className="font-extrabold tracking-tight text-slate-900">
                    MARKETING
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right image + bubble */}
          <div className="lg:col-span-5">
            <div className="relative overflow-hidden rounded-3xl bg-white shadow-[0_20px_60px_-30px_rgba(15,23,42,0.35)] ring-1 ring-[#cac6c6]">
              <img
                src={teamImage}
                alt="Team"
                className="h-[320px] w-full object-cover sm:h-[380px] lg:h-[520px]"
              />

              <div className="absolute left-6 top-6 rounded-full bg-yellow-200 px-4 py-3 text-sm font-extrabold text-slate-900 shadow-sm ring-1 ring-yellow-300">
                Platz für <span className="underline">Karriere</span> machen!
              </div>
            </div>


          </div>
        </div>
      </div>
    </section>
  );
}
